import { useState } from 'react';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import Chip from '@mui/material/Chip';
import Skeleton from '@mui/material/Skeleton';
import Stack from '@mui/material/Stack';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';
import Toolbar from '@mui/material/Toolbar';
import Typography from '@mui/material/Typography';
import AddIcon from '@mui/icons-material/Add';

import { useUsuarios } from '../../shared/api/queries.ts';
import type { Usuario } from '../../shared/api/tipos.ts';
import { formatarDataHora } from '../../shared/format.ts';
import { useSessao } from '../auth/sessao.tsx';
import { DialogoUsuario } from './DialogoUsuario.tsx';

export function Usuarios() {
  const { data, isPending, isError, error } = useUsuarios();
  const { usuario: eu } = useSessao();

  const [emEdicao, setEmEdicao] = useState<Usuario | undefined>(undefined);
  const [dialogoAberto, setDialogoAberto] = useState(false);

  const usuarios = data?.itens;

  function abrirNovo() {
    setEmEdicao(undefined);
    setDialogoAberto(true);
  }

  function abrirEdicao(usuario: Usuario) {
    setEmEdicao(usuario);
    setDialogoAberto(true);
  }

  return (
    <Stack spacing={2}>
      <Card>
        <Toolbar sx={{ gap: 2, flexWrap: 'wrap', py: 1.5 }}>
          <Box sx={{ flexGrow: 1, minWidth: 180 }}>
            <Typography variant="h2">Usuários</Typography>
            <Typography variant="body2" color="text.secondary">
              Quem pode entrar no sistema. Clique em um usuário para editar.
            </Typography>
          </Box>

          <Button variant="contained" startIcon={<AddIcon />} onClick={abrirNovo}>
            Novo usuário
          </Button>
        </Toolbar>

        <CardContent sx={{ pt: 0 }}>
          {isError ? (
            <Alert severity="error">
              Não foi possível carregar os usuários. {mensagemDoErro(error)}
            </Alert>
          ) : isPending ? (
            <Stack spacing={1} sx={{ py: 1 }}>
              {Array.from({ length: 4 }, (_, indice) => (
                <Skeleton key={indice} variant="rounded" height={40} />
              ))}
            </Stack>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Nome</TableCell>
                    <TableCell>Usuário</TableCell>
                    <TableCell>Papel</TableCell>
                    <TableCell>Último acesso</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {(usuarios ?? []).map((usuario) => (
                    <LinhaUsuario
                      key={usuario.id}
                      usuario={usuario}
                      souEu={usuario.id === eu?.id}
                      aoAbrir={() => abrirEdicao(usuario)}
                    />
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        </CardContent>
      </Card>

      <DialogoUsuario
        aberto={dialogoAberto}
        usuario={emEdicao}
        aoFechar={() => setDialogoAberto(false)}
      />
    </Stack>
  );
}

function LinhaUsuario({
  usuario,
  souEu,
  aoAbrir,
}: {
  usuario: Usuario;
  souEu: boolean;
  aoAbrir: () => void;
}) {
  return (
    <TableRow
      hover
      onClick={aoAbrir}
      // A linha é o controle: precisa ser alcançável e acionável pelo teclado
      // como um botão, igual às outras listagens do sistema.
      tabIndex={0}
      role="button"
      aria-label={`Editar ${usuario.nome}`}
      onKeyDown={(evento) => {
        if (evento.key === 'Enter' || evento.key === ' ') {
          evento.preventDefault();
          aoAbrir();
        }
      }}
      sx={{
        cursor: 'pointer',
        opacity: usuario.ativo ? 1 : 0.6,
        '&:focus-visible': { outline: 2, outlineColor: 'primary.main', outlineOffset: -2 },
      }}
    >
      <TableCell>
        <Stack direction="row" spacing={1} sx={{ alignItems: 'center' }}>
          <span>{usuario.nome}</span>
          {souEu && <Chip size="small" label="você" variant="outlined" />}
          {!usuario.ativo && <Chip size="small" label="Inativo" variant="outlined" />}
        </Stack>
      </TableCell>

      <TableCell sx={{ fontFamily: 'monospace' }}>{usuario.usuario}</TableCell>

      <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
        {usuario.papel === 'administrador' ? (
          <Chip size="small" color="primary" label="Administrador" />
        ) : usuario.papel === 'contador' ? (
          // O contador aparece aqui para a lista ser a lista inteira de quem
          // entra; quem o cria e troca a senha dele é a tela da contabilidade.
          <Chip size="small" color="info" label="Contador" variant="outlined" />
        ) : (
          <Chip size="small" label="Usuário" variant="outlined" />
        )}
      </TableCell>

      <TableCell sx={{ width: '1%', whiteSpace: 'nowrap' }}>
        {usuario.ultimoAcessoEm === null ? (
          <Typography variant="body2" color="text.secondary">
            nunca entrou
          </Typography>
        ) : (
          formatarDataHora(usuario.ultimoAcessoEm)
        )}
      </TableCell>
    </TableRow>
  );
}

function mensagemDoErro(erro: unknown): string {
  return erro instanceof Error ? erro.message : '';
}
